import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { trainingAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  GraduationCap, 
  Clock, 
  Play, 
  CheckCircle, 
  Search,
  Filter,
  BookOpen,
  Award,
  TrendingUp,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

const TrainingCard = ({ module, progress, onStart }) => {
  const difficultyColors = {
    beginner: 'bg-emerald-100 text-emerald-700',
    intermediate: 'bg-amber-100 text-amber-700',
    advanced: 'bg-red-100 text-red-700'
  };

  const currentProgress = progress?.progress || 0;
  const isCompleted = progress?.completed || false;

  return (
    <Card className="training-card overflow-hidden" data-testid={`training-card-${module.id}`}>
      <div className="relative">
        <img 
          src={module.thumbnail || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400'} 
          alt={module.title}
          className="training-card-image"
        />
        {isCompleted && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-emerald-500 text-white">
              <CheckCircle className="w-3 h-3 mr-1" />
              Completed
            </Badge>
          </div>
        )}
      </div>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <Badge variant="outline" className="text-xs">
            {module.category}
          </Badge>
          <Badge className={`text-xs ${difficultyColors[module.difficulty]}`}>
            {module.difficulty}
          </Badge>
        </div>
        <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {module.title}
        </h3>
        <p className="text-sm text-slate-500 mb-4 line-clamp-2">
          {module.description}
        </p>
        <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{module.duration} min</span>
          </div>
          {currentProgress > 0 && !isCompleted && (
            <span className="font-medium text-indigo-600">{currentProgress}% complete</span>
          )}
        </div>
        {currentProgress > 0 && !isCompleted && (
          <Progress value={currentProgress} className="h-1.5 mb-4" />
        )}
        <Button 
          onClick={() => onStart(module)}
          className={`w-full ${isCompleted ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
          data-testid={`start-training-${module.id}`}
        >
          {isCompleted ? (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Review Course
            </>
          ) : currentProgress > 0 ? (
            <>
              <Play className="w-4 h-4 mr-2" />
              Continue
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Start Course
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

const Training = () => {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchData();
  }, [user?.employee_id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [modulesRes, progressRes] = await Promise.all([
        trainingAPI.getModules(),
        trainingAPI.getProgress(user?.employee_id || '100001')
      ]);
      setModules(modulesRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Training fetch error:', error);
      toast.error('Failed to load training modules');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTraining = async (module) => {
    setSelectedModule(module);
    
    // Update progress if not started
    const existingProgress = progress.find(p => p.module_id === module.id);
    if (!existingProgress) {
      try {
        await trainingAPI.updateProgress({
          employee_id: user?.employee_id || '100001',
          module_id: module.id,
          progress: 10
        });
        fetchData();
      } catch (error) {
        console.error('Progress update error:', error);
      }
    }
  };

  const handleCompleteModule = async () => {
    if (!selectedModule) return;
    
    try {
      await trainingAPI.updateProgress({
        employee_id: user?.employee_id || '100001',
        module_id: selectedModule.id,
        progress: 100,
        score: Math.floor(Math.random() * 20) + 80,
        completed: true
      });
      toast.success('Congratulations! Course completed!');
      setSelectedModule(null);
      fetchData();
    } catch (error) {
      console.error('Complete module error:', error);
      toast.error('Failed to complete module');
    }
  };

  const getModuleProgress = (moduleId) => {
    return progress.find(p => p.module_id === moduleId);
  };

  const filteredModules = modules.filter(module => {
    const matchesSearch = module.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          module.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'in-progress') {
      const prog = getModuleProgress(module.id);
      return matchesSearch && prog && !prog.completed && prog.progress > 0;
    }
    if (activeTab === 'completed') {
      const prog = getModuleProgress(module.id);
      return matchesSearch && prog?.completed;
    }
    return matchesSearch;
  });

  const completedCount = progress.filter(p => p.completed).length;
  const inProgressCount = progress.filter(p => !p.completed && p.progress > 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading training modules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="training-page">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Total Courses</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {modules.length}
                </p>
              </div>
              <BookOpen className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">In Progress</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {inProgressCount}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Completed</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {completedCount}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-violet-100 text-sm">Certificates</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {completedCount}
                </p>
              </div>
              <Award className="w-10 h-10 text-violet-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white"
            data-testid="training-search"
          />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="all" data-testid="tab-all">All Courses</TabsTrigger>
            <TabsTrigger value="in-progress" data-testid="tab-in-progress">In Progress</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Course Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredModules.map((module) => (
          <TrainingCard
            key={module.id}
            module={module}
            progress={getModuleProgress(module.id)}
            onStart={handleStartTraining}
          />
        ))}
      </div>

      {filteredModules.length === 0 && (
        <div className="text-center py-12">
          <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No courses found</h3>
          <p className="text-slate-500">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Training Modal */}
      <Dialog open={!!selectedModule} onOpenChange={() => setSelectedModule(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedModule?.title}
            </DialogTitle>
            <DialogDescription>
              {selectedModule?.category} • {selectedModule?.duration} minutes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden">
              <img 
                src={selectedModule?.thumbnail} 
                alt={selectedModule?.title}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-slate-600">{selectedModule?.description}</p>
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <div className="flex items-center gap-4">
                <Badge className={`${
                  selectedModule?.difficulty === 'beginner' ? 'bg-emerald-100 text-emerald-700' :
                  selectedModule?.difficulty === 'intermediate' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {selectedModule?.difficulty}
                </Badge>
                <div className="flex items-center gap-1 text-amber-500">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                  <span className="text-slate-600 ml-1">4.8</span>
                </div>
              </div>
              <Button 
                onClick={handleCompleteModule}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="complete-training-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as Complete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Training;
